import React, { useCallback, useEffect, useState } from "react";
import { Routes, Route, NavLink, Navigate, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tag } from "@/components/Layout";
import { Building2, FileText, Boxes, MessageSquare, FileSpreadsheet, LogOut, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDate, formatDateTime } from "@/lib/dateFormat";

const NAV = [
  { to: "/vendor",          label: "Purchase orders", icon: ShoppingCart, end: true, testid: "vendor-nav-pos" },
  { to: "/vendor/bom",      label: "BOM lines",       icon: Boxes,        testid: "vendor-nav-bom" },
  { to: "/vendor/messages", label: "Messages",        icon: MessageSquare,testid: "vendor-nav-messages" },
  { to: "/vendor/documents",label: "Documents",       icon: FileText,     testid: "vendor-nav-docs" },
];

export default function VendorPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="vendor-portal-shell">
      <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center gap-3">
        <Building2 size={18} className="text-[#F54800]" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Vendor portal</div>
          <div className="font-display font-bold text-sm truncate">{user?.company || user?.name}</div>
        </div>
        <Tag tone="green">active</Tag>
        <button onClick={() => { logout().then(() => nav("/login")); }} className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="vendor-logout"><LogOut size={11} /> Sign out</button>
      </header>
      <div className="flex-1 flex">
        {/* Sidebar desktop */}
        <nav className="hidden md:flex w-56 bg-white border-r border-slate-200 flex-col p-3">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `inline-flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-semibold ${isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
              data-testid={n.testid}>
              <n.icon size={13} /> {n.label}
            </NavLink>
          ))}
        </nav>
        {/* Mobile top tabs */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 grid grid-cols-4">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 text-[10px] ${isActive ? "text-[#F54800]" : "text-slate-500"}`}>
              <n.icon size={14} /> {n.label.split(" ")[0]}
            </NavLink>
          ))}
        </nav>
        <main className="flex-1 min-w-0 pb-16 md:pb-0">
          <Routes>
            <Route index element={<VendorPOs />} />
            <Route path="pos/:po_id" element={<VendorPODetail />} />
            <Route path="bom" element={<VendorBOM />} />
            <Route path="messages" element={<VendorMessages />} />
            <Route path="documents" element={<VendorDocuments />} />
            <Route path="*" element={<Navigate to="/vendor" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

const PO_STATUS_TONE = { sent: "blue", acknowledged: "yellow", invoice_uploaded: "orange", closed: "green" };

function VendorPOs() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/vendor/pos").then(({ data }) => setRows(data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, []);
  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto fade-in" data-testid="vendor-pos-page">
      <h1 className="font-display text-2xl text-slate-900 mb-1">Purchase orders</h1>
      <p className="text-xs text-slate-500 mb-6">All POs the studio has issued to you. Acknowledge, upload invoices when shipped.</p>
      {loading ? <div className="text-sm text-slate-500">Loading…</div> : rows.length === 0 ? (
        <div className="card-flat p-12 text-center text-sm text-slate-500" data-testid="vendor-pos-empty">No purchase orders yet.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((po) => (
            <button key={po.id} onClick={() => nav(`/vendor/pos/${po.id}`)} className="w-full card-flat p-4 text-left hover:bg-slate-50 transition-colors" data-testid={`vendor-po-${po.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[10px] text-slate-400 uppercase">{po.po_number}</div>
                  <div className="font-display font-bold text-sm text-slate-900 truncate">{po.title}</div>
                  <div className="text-[11px] text-slate-500 mt-1">{(po.line_items || []).length} line(s) · {po.payment_terms}</div>
                </div>
                <div className="text-right shrink-0">
                  <Tag tone={PO_STATUS_TONE[po.status] || "slate"}>{(po.status || "").replace("_", " ")}</Tag>
                  <div className="font-mono font-bold text-sm text-slate-900 mt-1">{po.currency} {Number(po.total || 0).toLocaleString("en-IN")}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function VendorPODetail() {
  const { po_id } = useParams();
  const nav = useNavigate();
  const [po, setPo] = useState(null);
  const [ackOpen, setAckOpen] = useState(false);
  const [invOpen, setInvOpen] = useState(false);
  const [ackNote, setAckNote] = useState("");
  const [inv, setInv] = useState({ invoice_number: "", invoice_file_url: "", invoice_file_name: "", amount: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/vendor/pos/${po_id}`); setPo(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [po_id]);
  useEffect(() => { load(); }, [load]);

  const acknowledge = useCallback(async () => {
    setBusy(true);
    try { await api.post(`/vendor/pos/${po_id}/acknowledge`, { note: ackNote || null }); toast.success("Acknowledged"); setAckOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }, [po_id, ackNote, load]);

  const uploadInvoice = async () => {
    if (!inv.invoice_number.trim() || !inv.invoice_file_url.trim()) { toast.error("Invoice number and Google Drive link are required"); return; }
    setBusy(true);
    try {
      await api.post(`/vendor/pos/${po_id}/invoice`, {
        invoice_number: inv.invoice_number,
        invoice_file_url: inv.invoice_file_url,
        invoice_file_name: "Google Drive link",
        amount: inv.amount ? Number(inv.amount) : null,
        currency: po?.currency, note: inv.note,
      });
      toast.success("Invoice submitted");
      setInvOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!po) return <div className="p-6 text-sm text-slate-500">Loading…</div>;

  return (
    <div className="p-4 sm:p-8 max-w-3xl mx-auto fade-in" data-testid={`vendor-po-detail-${po.id}`}>
      <button onClick={() => nav("/vendor")} className="text-[11px] text-slate-500 hover:text-slate-900 mb-3">← Back to POs</button>
      <div className="card-flat p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] text-slate-400 uppercase">{po.po_number}</div>
            <h1 className="font-display text-xl text-slate-900">{po.title}</h1>
          </div>
          <Tag tone={PO_STATUS_TONE[po.status] || "slate"} data-testid="vendor-po-status">{po.status?.replace("_", " ")}</Tag>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-[11px]">
          <div><div className="label-overline">Payment</div><div className="font-semibold">{po.payment_terms}</div></div>
          {po.delivery_terms && <div><div className="label-overline">Delivery</div><div className="font-semibold">{po.delivery_terms}</div></div>}
          <div><div className="label-overline">Total</div><div className="font-mono font-bold">{po.currency} {Number(po.total || 0).toLocaleString("en-IN")}</div></div>
        </div>

        <h3 className="font-display font-bold text-sm mt-6 mb-2">Line items</h3>
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr><th className="text-left px-3 py-2">Description</th><th className="text-right px-3 py-2">Qty</th><th className="text-right px-3 py-2">Unit</th><th className="text-right px-3 py-2">Amount</th></tr>
            </thead>
            <tbody>
              {(po.line_items || []).map((li, i) => (
                <tr key={`po-line-${po.id || "x"}-${i}`} className="border-t border-slate-100" data-testid={`vendor-po-line-${i}`}>
                  <td className="px-3 py-2">{li.description}</td>
                  <td className="px-3 py-2 text-right">{li.qty}</td>
                  <td className="px-3 py-2 text-right font-mono">{li.unit_price}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">{(li.qty * li.unit_price).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {po.notes && <div className="mt-4 text-[11px] text-slate-600 italic">Notes: {po.notes}</div>}

        {po.invoice && (
          <div className="mt-5 p-3 bg-slate-50 rounded-sm text-[11px]">
            <div className="font-semibold mb-1">Invoice on file: {po.invoice.number}</div>
            <a href={po.invoice.file_url} target="_blank" rel="noreferrer noopener" className="text-[#F54800] hover:underline">{po.invoice.file_name || "Download"}</a>
          </div>
        )}

        <div className="flex items-center gap-2 mt-6">
          {po.status === "sent" && (
            <Button onClick={() => setAckOpen(true)} className="rounded-sm bg-slate-900 hover:bg-slate-700 text-white" data-testid="vendor-ack-btn">Acknowledge PO</Button>
          )}
          {(po.status === "acknowledged" || po.status === "sent") && (
            <Button onClick={() => setInvOpen(true)} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="vendor-invoice-btn">Submit invoice</Button>
          )}
        </div>
      </div>

      {/* Acknowledge dialog */}
      <Dialog open={ackOpen} onOpenChange={setAckOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-display">Acknowledge PO</DialogTitle><DialogDescription>Confirm you&apos;ve received this PO. You can add a quick note.</DialogDescription></DialogHeader>
          <Textarea rows={3} className="rounded-sm resize-none" placeholder="Optional message to studio (delivery ETA, clarifications…)" value={ackNote} onChange={(e) => setAckNote(e.target.value)} data-testid="vendor-ack-note" />
          <DialogFooter>
            <Button onClick={acknowledge} disabled={busy} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="vendor-ack-confirm">Acknowledge</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice dialog */}
      <Dialog open={invOpen} onOpenChange={setInvOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-display">Submit invoice</DialogTitle><DialogDescription>Paste your invoice's Google Drive link & enter the number.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="label-overline">Invoice number *</Label><Input className="rounded-sm mt-1" value={inv.invoice_number} onChange={(e) => setInv({ ...inv, invoice_number: e.target.value })} data-testid="vendor-inv-number" /></div>
            <div><Label className="label-overline">Amount</Label><Input type="number" className="rounded-sm mt-1" value={inv.amount} onChange={(e) => setInv({ ...inv, amount: e.target.value })} placeholder={po.total} data-testid="vendor-inv-amount" /></div>
            <div>
              <Label className="label-overline">Invoice — Google Drive link *</Label>
              <Input className="rounded-sm mt-1" placeholder="https://drive.google.com/file/d/…" value={inv.invoice_file_url} onChange={(e) => setInv({ ...inv, invoice_file_url: e.target.value })} data-testid="vendor-inv-file" />
            </div>
            <div><Label className="label-overline">Note</Label><Textarea rows={2} className="rounded-sm mt-1 resize-none" value={inv.note} onChange={(e) => setInv({ ...inv, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button onClick={uploadInvoice} disabled={busy || !inv.invoice_file_url.trim()} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="vendor-inv-submit">Submit invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VendorBOM() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/vendor/bom-items").then(({ data }) => setRows(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto fade-in" data-testid="vendor-bom-page">
      <h1 className="font-display text-2xl text-slate-900 mb-1">Shared BOM lines</h1>
      <p className="text-xs text-slate-500 mb-6">Specs and drawings the studio has shared with you — quote-ready when you are.</p>
      {rows.length === 0 ? (
        <div className="card-flat p-12 text-center text-sm text-slate-500" data-testid="vendor-bom-empty">Nothing shared yet. POs will populate here automatically.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((it, i) => (
            <div key={`${it.project_id}-${it.bom_item_id}-${i}`} className="card-flat p-4" data-testid={`vendor-bom-${it.bom_item_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display font-bold text-sm">{it.part_name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">Qty {it.qty} · Lead {it.lead_time_days || "?"}d{it.via_po ? ` · via ${it.via_po}` : ""}</div>
                  {it.notes && <div className="text-[11px] text-slate-600 mt-1">{it.notes}</div>}
                  {it.share_note && <div className="text-[11px] text-slate-500 italic mt-1">Studio note: {it.share_note}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VendorMessages() {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/vendor/messages"); setRows(data); } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  const send = useCallback(async () => {
    if (!draft.trim()) return;
    setSending(true);
    try { await api.post("/vendor/messages", { body: draft }); setDraft(""); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  }, [draft, load]);
  return (
    <div className="p-4 sm:p-8 max-w-3xl mx-auto fade-in flex flex-col" style={{ height: "calc(100vh - 60px)" }} data-testid="vendor-messages-page">
      <h1 className="font-display text-2xl text-slate-900 mb-1">Messages</h1>
      <p className="text-xs text-slate-500 mb-4">Private thread with the Analogy studio team.</p>
      <div className="flex-1 overflow-y-auto card-flat p-4 space-y-2 mb-3">
        {rows.length === 0 ? <div className="text-xs text-slate-400 text-center py-12">No messages yet — say hi to the studio.</div> :
          rows.map((m) => (
            <div key={m.id} className={`text-xs ${m.user_id === "system" ? "italic text-slate-500" : ""}`} data-testid={`vendor-msg-${m.id}`}>
              <div className="font-semibold text-slate-900">{m.user_name}</div>
              <div className="text-slate-700">{m.body}</div>
              <div className="text-[10px] text-slate-400">{formatDateTime(m.created_at)}</div>
            </div>
          ))
        }
      </div>
      <div className="flex items-end gap-2">
        <Textarea rows={2} className="rounded-sm resize-none flex-1" placeholder="Type your message…" value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="vendor-msg-input" />
        <Button onClick={send} disabled={sending || !draft.trim()} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="vendor-msg-send">Send</Button>
      </div>
    </div>
  );
}

function VendorDocuments() {
  const [docs, setDocs] = useState([]);
  useEffect(() => { api.get("/vendor/documents").then(({ data }) => setDocs(data)).catch((e) => toast.error(formatApiError(e))); }, []);
  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto fade-in" data-testid="vendor-docs-page">
      <h1 className="font-display text-2xl text-slate-900 mb-1">Documents</h1>
      <p className="text-xs text-slate-500 mb-6">Your NDA, POs and invoices.</p>
      {docs.length === 0 ? (
        <div className="card-flat p-12 text-center text-sm text-slate-500">No documents yet.</div>
      ) : (
        <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.id} className="card-flat p-4 flex items-center gap-3" data-testid={`vendor-doc-${d.id}`}>
              <FileSpreadsheet size={16} className="text-slate-400" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{d.title}</div>
                <div className="text-[11px] text-slate-500">{d.kind}{d.signed_at ? ` · signed ${formatDate(d.signed_at)}` : d.uploaded_at ? ` · uploaded ${formatDate(d.uploaded_at)}` : d.created_at ? ` · ${formatDate(d.created_at)}` : ""}</div>
              </div>
              {d.file_url && <a href={d.file_url} target="_blank" rel="noreferrer noopener" className="text-[#F54800] text-[11px] hover:underline">Open</a>}
              {d.total && <div className="font-mono text-xs">{d.currency} {Number(d.total).toLocaleString("en-IN")}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
